import React from "react";
import {
  Stack,
  Alert,
  Select,
  Card,
  Group,
  Text,
  List,
  Loader,
  Button,
} from "@mantine/core";
import { IconPackage } from "@tabler/icons-react";
import { ServicePackage } from "../../../../../types/packages";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { formatQty } from "../../../../../utils/quantityUtils";
import {
  formatLocalDate,
  classifySpecial,
} from "../../../../settings/packages/specialDates";

interface PackageSpecialPickerProps {
  /** Packages + specials the salon offers (from getServicePackages). */
  packages: ServicePackage[];
  isLoading: boolean;
  selectedPackageId: string | null;
  onSelectPackage: (id: string | null) => void;
  /** Fires the sell/add action for the selected package. */
  onSubmit: () => void;
  submitLabel: string;
  isSubmitting: boolean;
  error?: string | null;
  onClearError?: () => void;
  /**
   * Salon IANA timezone. Specials are filtered to their ACTIVE window against
   * the salon's local day (matching the backend sell gate) so an expired or
   * not-yet-started special is never offered. Evergreen packages always show.
   */
  timezone?: string | null;
}

/**
 * Shared "pick a package or special to sell" surface — a grouped Select
 * (Specials first, then Packages), a detail card, and a submit button. Used by
 * Quick Sale (standalone sale) and the appointment TransactionDetailModal
 * (package added to the appointment's bill), so both stay in lockstep.
 */
export default function PackageSpecialPicker({
  packages,
  isLoading,
  selectedPackageId,
  onSelectPackage,
  onSubmit,
  submitLabel,
  isSubmitting,
  error,
  onClearError,
  timezone,
}: PackageSpecialPickerProps) {
  const toOption = (pkg: ServicePackage) => ({
    value: pkg.id,
    label: `${pkg.name} — ${formatCurrency(pkg.price)}`,
  });

  // Specials only appear inside their active window; packages always appear.
  const specialOptions = packages
    .filter(
      (pkg) =>
        pkg.kind === "special" && classifySpecial(pkg, timezone) === "active",
    )
    .map(toOption);
  const regularOptions = packages
    .filter((pkg) => pkg.kind !== "special")
    .map(toOption);
  const packageOptions = [
    ...(specialOptions.length > 0
      ? [{ group: "Specials", items: specialOptions }]
      : []),
    ...(regularOptions.length > 0
      ? [{ group: "Packages", items: regularOptions }]
      : []),
  ];
  const hasSellable = specialOptions.length > 0 || regularOptions.length > 0;

  const selectedPackage = packages.find((pkg) => pkg.id === selectedPackageId);

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" withCloseButton onClose={onClearError}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Group justify="center" py="lg">
          <Loader size="sm" color="var(--brand-teal)" />
          <Text size="sm" c="dimmed">
            Loading packages…
          </Text>
        </Group>
      ) : !hasSellable ? (
        <Alert icon={<IconPackage size={16} />} color="gray" variant="light">
          No packages available. Create one in Settings → Packages & Specials
          first.
        </Alert>
      ) : (
        <>
          <Select
            label="Package"
            placeholder="Select a package to sell"
            data={packageOptions}
            value={selectedPackageId}
            onChange={onSelectPackage}
            searchable
          />

          {selectedPackage && (
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>{selectedPackage.name}</Text>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" c="dimmed">
                      Price
                    </Text>
                    <Text fw={600} c="var(--brand-teal)">
                      {formatCurrency(selectedPackage.price)}
                    </Text>
                  </Group>
                </Group>
                {selectedPackage.description && (
                  <Text size="sm" c="dimmed">
                    {selectedPackage.description}
                  </Text>
                )}
                {selectedPackage.kind === "special" &&
                  (selectedPackage.effective_start_date ||
                    selectedPackage.effective_end_date) && (
                    <Text size="xs" c="dimmed">
                      Effective{" "}
                      {formatLocalDate(selectedPackage.effective_start_date)} –{" "}
                      {formatLocalDate(selectedPackage.effective_end_date)}
                    </Text>
                  )}
                <Text size="xs" c="dimmed">
                  {selectedPackage.validity_days
                    ? `Expires ${selectedPackage.validity_days} days after purchase`
                    : "Never expires"}
                </Text>
                <List size="sm" spacing={2}>
                  {selectedPackage.items.map((item) => (
                    <List.Item key={item.salon_service_id}>
                      {formatQty(item.quantity)} x {item.service_name}
                    </List.Item>
                  ))}
                </List>
              </Stack>
            </Card>
          )}

          <Button
            onClick={onSubmit}
            disabled={!selectedPackageId || isSubmitting}
            loading={isSubmitting}
            color="var(--brand-teal)"
            size="lg"
            fullWidth
          >
            {submitLabel}
          </Button>
        </>
      )}
    </Stack>
  );
}
